import { UserManager, type User } from 'oidc-client-ts'
import { useEffect, useMemo, useRef, useState } from 'react'
import { setValuesInternalAndWait } from '../../framework/actions'
import { SPARKY_FRAMEWORK } from '../../framework/constants'
import { history } from '../../framework/history'
import { clearStoredExpiredTsgTokens } from '../../framework/tsg'
import { __info, __isDebugOn } from '../../init/debug'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { alertAndDisableTiming, clearOidcSessionStorage } from '../common/utils'
import { getShellOverrideConfigKeyByPath } from '../../framework/utils'
import { unsetSession } from '../mainOneappNew/fawkesUtils'
import { DEFAULT_OIDC_CONFIG, getOidcConfig } from '../../framework/oidcConfig'
import { AuthenticatingTimeout, InactivityLogout, NotAuthenticated } from '../common/FullscreenError'

import type { ReactElement, VFC } from 'react'
import type { SparkySettings } from '../../framework/microAppTypes'
import type { FrameworkVars, OidcState } from '../../framework/stateTypes'

const OidcLoading: VFC<{}> = () => {
  const TIMEOUT = 10000 // 10s
  const [loading, setLoading] = useState<boolean>(true)
  useEffect(() => {
    const ts = setTimeout(() => {
      setLoading(false)
    }, TIMEOUT)
    return () => {
      clearTimeout(ts)
    }
  })
  return loading ? <LoadingPlaceholder /> : <AuthenticatingTimeout />
}

const storePathOverride = (config: FrameworkVars['shell_path_overrides']) => {
  if (config) {
    const matchedPath = getShellOverrideConfigKeyByPath(config, window.location.pathname) || ''
    if (matchedPath.length > 1) { // ignore /
      sessionStorage.__sparky_path_override_key = matchedPath
    } // will remove after fully logged in
  }
}

const getCurrentUrl = (): string => {
  const url = window.location.href.replace(window.location.origin, '')
  if (url.includes(DEFAULT_OIDC_CONFIG.redirect_uri)) {
    return ''
  }
  return url
}

const OidcAuth: VFC<{ children: ReactElement | null, settings: SparkySettings }> = ({ children, settings }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasOidcError, setOidcError] = useState(false)
  const fxVarsRef = useRef<FrameworkVars>(settings.variables[SPARKY_FRAMEWORK])
  const userManager: UserManager | undefined = useMemo(() => {
    const config = getOidcConfig(fxVarsRef.current)
    if (config) {
      return new UserManager(config)
    }
  }, [])

  useEffect(() => {
    if (!userManager) {
      setOidcError(true)
      return
    }
    const fxVars = fxVarsRef.current
    const login = () => {
      const url = sessionStorage._sparky_oidc_state_url || getCurrentUrl()
      sessionStorage._sparky_oidc_state_url = url // backup
      return userManager.signinRedirect(url.length > 1 ? { state: { url } } : undefined)
    }
    const setOidcState = (user: User) => {
      setValuesInternalAndWait({
        oidc: Object.defineProperties({
          oidcUser: user,
        }, { // skip json.stringify
          login: {
            value: () => {
              storePathOverride(fxVars.shell_path_overrides)
              login()
            },
          },
          logout: {
            value: () => {
              userManager.signoutRedirect()
            },
          },
          userManager: {
            value: userManager,
          },
        }) as OidcState
      }).then(() => {
        setIsLoading(false)
      })
      if (sessionStorage.reloadRetry) {
        delete sessionStorage.reloadRetry
      }
    }
    if (window.location.pathname.includes(DEFAULT_OIDC_CONFIG.redirect_uri)) {
      userManager.signinRedirectCallback().then((user) => {
        console.log('User logged in', user)
        setIsLoading(false)
        if (sessionStorage._sparky_signin_redirect_retry) {
          delete sessionStorage._sparky_signin_redirect_retry
        }
        if (sessionStorage._sparky_oidc_state_url) {
          delete sessionStorage._sparky_oidc_state_url
        }
        setOidcState(user)
        history.replace((user.state as { url?: string } | undefined)?.url || '/')
      }, (err) => {
        setIsLoading(false)
        if (sessionStorage._sparky_signin_redirect_retry) {
          console.error('oidc signin callback error after retried', err)
          delete sessionStorage._sparky_signin_redirect_retry
          setOidcError(true)
          return
        }
        console.error('retry oidc signin after callback error', err)
        clearOidcSessionStorage()
        sessionStorage._sparky_signin_redirect_retry = '1'
        login() // retry redirect login once
      })
      return
    }
    const tryLogin = () => {
      if (fxVars.always_redirect_signin) {
        storePathOverride(fxVars.shell_path_overrides)
        return login()
      }
      const unsetFawkesTidAndLogin = () => { // fallback to login if failed
        unsetSession() // ensure previous tenant id been cleared
        clearStoredExpiredTsgTokens() // clear expired tokens in session, avoid invalid grant error
        if (window.location.search?.includes('tenantId')) {
          if (__isDebugOn()) {
            alertAndDisableTiming('Specify tenantId in url is not supported in TSG based env, please use tsg_id instead')
          }
          const url = new URL(window.location.href)
          url.searchParams.delete('tenantId')
          history.replace(`${url.pathname}${url.search.length > 1 ? url.search : ''}`)
        }
        storePathOverride(fxVars.shell_path_overrides)
        login() // do not clear session storage here, will reuse stored info without redirect
      }
      return userManager.signinSilent().then((user) => {
        if (user) {
          __info('user is logged in silently', user)
          setOidcState(user)
        }
        else {
          unsetFawkesTidAndLogin()
        }
      }, unsetFawkesTidAndLogin)
    }
    userManager.getUser().then((user) => {
      if (user && !user.expired) {
        __info('User is already logged in', user)
        setOidcState(user)
      }
      else {
        __info('try logged in', user)
        return tryLogin()
      }
    }, (err) => {
      console.error('error getting user', err)
      return tryLogin()
    })
  }, [userManager])

  const { inactivityLogout, inactivityLogoutMessage } = userManager as { inactivityLogout?: boolean, inactivityLogoutMessage?: string }
  if (inactivityLogout) {
    return <InactivityLogout message={inactivityLogoutMessage} />
  }

  if (hasOidcError) {
    return <NotAuthenticated />
  }

  if (isLoading) {
    return <OidcLoading />
  }

  return children
}

export default OidcAuth
